<template>
  <!-- 合同管理列表 -->
  <div class="contract-list">
    <list-page>
      <search-condition>
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="合同编号/合同名称" clearable @keydown.enter="resetSearch">
            <template #prefix>关键字</template>
          </el-input>
          <customer-select v-model="searchInfo.customerId" placeholder="请选择客户">
            <template #prefix>客户</template>
          </customer-select>
          <el-select v-model="searchInfo.contractType" placeholder="全部合同类型" clearable>
            <template #prefix>合同类型</template>
            <el-option v-for="(label, value) in contractTypeMap" :key="value" :label="label" :value="Number(value)"/>
          </el-select>
          <el-select v-model="searchInfo.contractStatus" placeholder="全部状态" clearable>
            <template #prefix>状态</template>
            <el-option v-for="(item, value) in contractStatusMap" :key="value" :label="item.text" :value="Number(value)"/>
          </el-select>
          <employee-select v-model="searchInfo.salesId" placeholder="全部销售人员">
            <template #prefix>销售人员</template>
          </employee-select>
          <!-- 时间范围按签约日期过滤（未填签约日期时退回创建时间，与统计口径一致） -->
          <yq-date-range v-model:start-time="searchInfo.startTime" v-model:end-time="searchInfo.endTime">
            <template #prefix>
              <span>签约时间</span>
            </template>
          </yq-date-range>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="toEdit()">新建合同</page-button>
          <page-button type="write" el-type="warning" :disabled="importStatus.running"
                       @click="showImport = true">{{ importStatus.running ? '导入中...' : '导入合同' }}</page-button>
        </template>
      </search-condition>

      <list-table>
        <!-- 导入客户年费表：相邻同客户行合并为一份合同 -->
        <import-component ref="importComponent" v-model="showImport" title="导入合同"
                          :import-by-excel="handleImport" @export-temp="downloadTemplate"/>
        <yq-table :data="dataList" height="100%" stripe border page-name="contractList">
          <yq-table-column label="合同编号" prop="contractNumber" min-width="170rem">
            <template v-slot="{row}">
              <a @click="toDetail(row)">{{ row.contractNumber || '—' }}</a>
            </template>
          </yq-table-column>
          <yq-table-column label="合同名称" prop="contractName" min-width="200rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="客户名称" prop="customerName" min-width="200rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">{{ row.customerName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="合同类型" prop="contractType" width="110rem" align="center">
            <template v-slot="{row}">{{ contractTypeMap[row.contractType] || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="合同金额(元)" prop="amount" width="130rem" align="right">
            <template v-slot="{row}">{{ fmtMoney(row.amount) }}</template>
          </yq-table-column>
          <yq-table-column label="已回款(元)" prop="amountReceived" width="130rem" align="right">
            <template v-slot="{row}">{{ fmtMoney(row.amountReceived) }}</template>
          </yq-table-column>
          <yq-table-column label="回款进度" prop="receiptProgress" width="140rem" align="center">
            <template v-slot="{row}">
              <el-progress :percentage="progress(row.receiptProgress)" :stroke-width="10"/>
            </template>
          </yq-table-column>
          <yq-table-column label="签约日期" prop="billDate" width="120rem" align="center">
            <template v-slot="{row}">{{ dateOnly(row.billDate) }}</template>
          </yq-table-column>
          <yq-table-column label="到期日期" prop="expireDate" width="120rem" align="center">
            <template v-slot="{row}">{{ dateOnly(row.expireDate) }}</template>
          </yq-table-column>
          <yq-table-column label="负责人" prop="salesName" width="110rem" align="center">
            <template v-slot="{row}">{{ row.salesName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="状态" prop="contractStatus" width="100rem" align="center">
            <template v-slot="{row}">
              <el-tag size="small" :type="statusOf(row.contractStatus).type">{{ statusOf(row.contractStatus).text }}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="260rem" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button-group label="更多操作">
                <table-button @click="toDetail(row)">查看详情</table-button>
                <table-button type="write" @click="toEdit(row)">编辑</table-button>
                <table-button type="write" @click="openReceiptDialog(row)">收款</table-button>
                <table-button type="delete" el-type="danger" message="是否确认删除该合同" @click="toDelete(row)">删除</table-button>
              </table-button-group>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 收款弹窗 -->
    <el-dialog v-model="showReceiptDialog" title="合同收款" dialog-width="1" :close-on-click-modal="false">
      <form-items :data="receiptForm" :columns="1" label-width="3rem" icon-width="0">
        <form-item label="合同编号">{{ receiptForm.contractNumber || '—' }}</form-item>
        <form-item label="合同金额">{{ fmtMoney(receiptForm.contractAmount) }} 元</form-item>
        <form-item label="本次收款金额" :required="true">
          <el-input-number v-model="receiptForm.amountReceived" :min="0" :precision="2" controls-position="right" style="width: 100%"/>
        </form-item>
        <form-item label="备注">
          <el-input v-model="receiptForm.remark" type="textarea" :rows="2" placeholder="备注信息"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showReceiptDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveReceipt">确定</page-button>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import TableButtonGroup from "@/components/h5/button/TableButtonGroup.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import CustomerSelect from "@/components/h5/partner/CustomerSelect.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
import ImportComponent from "@/components/h5/ImportComponent.vue";
import {useRoute, useRouter} from "vue-router";
import excelUtil from "@/util/excelUtil";
import contractApi from "@/api/dz-crm/partner/contractApi";

const CONTRACT_TYPE_MAP = {1: "框架协议", 2: "销售合同", 3: "代理协议", 4: "服务合同"};
// 状态按 100 倍数递增：0草稿 100执行中 200已完成 300已逾期
const CONTRACT_STATUS_MAP = {
  0: {text: "草稿", type: "info"},
  100: {text: "执行中", type: "primary"},
  200: {text: "已完成", type: "success"},
  300: {text: "已逾期", type: "danger"},
};

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ContractList",
  components: {
    ImportComponent, YqDateRange, EmployeeSelect, YqTable, CustomerSelect, TableButtonGroup, TableButton, PageButton,
    FormItems, FormItem, YqTableColumn, SearchCondition, FormPage, ListTable, ListPage,
  },
  data() {
    return {
      searchInfo: {
        pageNumber: 1,
        searchKey: "",
        customerId: "",
        contractType: "",
        contractStatus: "",
        salesId: "",
        startTime: "",
        endTime: "",
      },
      dataList: [],
      contractTypeMap: CONTRACT_TYPE_MAP,
      contractStatusMap: CONTRACT_STATUS_MAP,
      showReceiptDialog: false,
      receiptForm: {contractId: "", contractNumber: "", contractAmount: null, amountReceived: null, remark: ""},
      showImport: false,
      // 导入状态：后端异步导入，前端每5秒轮询一次
      importStatus: {running: false, status: "NONE"},
      importPollTimer: null,
      // 只有当前页面发起的导入（或进入页面时正在导入）才在完成时提示结果
      importPollingNotified: false,
    };
  },
  beforeRouteLeave(to, from, next) {
    this.stopImportPolling();
    next();
  },
  deactivated() {
    this.stopImportPolling();
  },
  activated() {
    // 页签缓存后回到本页，继续跟进后台导入状态
    this.initImportStatus();
  },
  created() {
    // 从销售目标页跳转过来时预置销售人员与目标周期（时间范围只取到日）
    const query = this.route.query || {};
    if (query.salesId) {
      this.searchInfo.salesId = query.salesId;
    }
    if (query.startTime) {
      this.searchInfo.startTime = String(query.startTime).slice(0, 10);
    }
    if (query.endTime) {
      this.searchInfo.endTime = String(query.endTime).slice(0, 10);
    }
    this.resetSearch();
    this.initImportStatus();
  },
  methods: {
    queryList() {
      // 空值不提交（Integer 字段传空串会绑定失败）；日期只取到日，与后端 yyyy-MM-dd 口径一致
      const params = {};
      Object.keys(this.searchInfo).forEach(key => {
        const value = this.searchInfo[key];
        // totalCount 是上一页返回的展示字段，不是查询条件
        if (value === "" || value === null || value === undefined || key === "totalCount") {
          return;
        }
        params[key] = value;
      });
      ["startTime", "endTime"].forEach(key => {
        if (params[key]) {
          params[key] = String(params[key]).slice(0, 10);
        }
      });
      contractApi.queryList(params).then(res => {
        this.dataList = res.records || [];
        this.searchInfo.totalCount = res.totalCount || 0;
      });
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    toDetail(row) {
      this.router.push(`/contract/list/detail?id=${row.id}`);
    },
    toEdit(row) {
      this.router.push(row ? `/contract/list/edit?id=${row.id}` : "/contract/list/edit");
    },
    openReceiptDialog(row) {
      this.receiptForm = {
        contractId: row.id,
        contractNumber: row.contractNumber,
        contractAmount: row.amount,
        amountReceived: null,
        remark: "",
      };
      this.showReceiptDialog = true;
    },
    saveReceipt() {
      if (!this.receiptForm.amountReceived || Number(this.receiptForm.amountReceived) <= 0) {
        this.$message.error("请填写本次收款金额");
        return;
      }
      contractApi.receipt(this.receiptForm).then(() => {
        this.$message.success("收款成功");
        this.showReceiptDialog = false;
        this.resetSearch();
      });
    },
    toDelete(row) {
      contractApi.delete(row.id).then(() => {
        this.$message.success("已删除");
        this.queryList();
      });
    },
    /** 下载导入模板 */
    downloadTemplate() {
      excelUtil.exportExcel("/basedata/contract/importTemplate", {}, "客户年费表导入模板.xlsx");
    },
    /** 上传回调：后台异步导入，接口立即返回，前端轮询导入状态 */
    handleImport(formData) {
      return contractApi.importExcel(formData).then(() => {
        this.$message.success("合同正在导入中，请稍后查询");
        this.showImport = false;
        this.importStatus = {running: true, status: "RUNNING"};
        this.importPollingNotified = true;
        // 清掉通用组件里选中的文件，下次打开不要还挂着上一次的文件
        if (this.$refs.importComponent) {
          this.$refs.importComponent.fileList = [];
          this.$refs.importComponent.file = "";
        }
        this.startImportPolling();
      }).catch(() => {
        // 上一次导入未完成等错误由请求拦截器提示，这里同步一下真实状态
        this.initImportStatus();
      });
    },
    /** 进入页面先查一次：如果后台正在导入，继续轮询 */
    initImportStatus() {
      contractApi.importStatus().then(status => {
        this.importStatus = status || {running: false, status: "NONE"};
        if (this.importStatus.running) {
          this.importPollingNotified = true;
          this.startImportPolling();
        }
      });
    },
    startImportPolling() {
      if (this.importPollTimer) {
        return;
      }
      this.importPollTimer = setInterval(this.pollImportStatus, 5000);
    },
    stopImportPolling() {
      if (this.importPollTimer) {
        clearInterval(this.importPollTimer);
        this.importPollTimer = null;
      }
    },
    pollImportStatus() {
      contractApi.importStatus().then(status => {
        this.importStatus = status || {running: false, status: "NONE"};
        if (this.importStatus.running) {
          return;
        }
        this.stopImportPolling();
        if (!this.importPollingNotified) {
          return;
        }
        this.importPollingNotified = false;
        if (status.status === "SUCCESS") {
          this.$message.success(`合同导入完成：合同 ${status.contractCount || 0} 份，明细 ${status.itemCount || 0} 条，跳过 ${status.skippedCount || 0} 行`);
          this.queryList();
        } else if (status.status === "FAILED") {
          this.$message.error("合同导入失败：" + (status.message || "请查看后台日志"));
        }
      }).catch(() => {
        // 轮询失败不停止，下一次继续
      });
    },
    statusOf(status) {
      return CONTRACT_STATUS_MAP[status] || {text: "—", type: "info"};
    },
    progress(value) {
      const n = Number(value) || 0;
      return Math.max(0, Math.min(100, Math.round(n * 100) / 100));
    },
    fmtMoney(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 0, maximumFractionDigits: 2});
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
.importing-tip {
  margin-left: 10px;
  font-size: 12px;
  color: var(--el-color-warning, #e6a23c);
}
</style>
